import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleCheckVerification = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
            navigate('/admin/setup');
        } else {
            setErrorMessage('メール認証がまだ完了していません。メール内のリンクをクリックしてください。');
        }
      } else {
          navigate('/login');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('ステータスの確認に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!auth.currentUser || resendStatus === 'sending') return;
    setResendStatus('sending');
    setErrorMessage(null);
    try {
        const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmail');
        await sendVerificationEmailFn({
            email: auth.currentUser.email,
            origin: window.location.origin
        });
        setResendStatus('sent');
        setTimeout(() => setResendStatus('idle'), 60000); 
    } catch (error: unknown) {
        console.error("Resend failed:", error);
        setResendStatus('error');
        const message = error instanceof Error ? error.message : '';
        if (message.includes('too-many-requests')) {
            setErrorMessage('送信回数の上限を超えました。しばらく時間を置いてから再試行してください。');
        } else {
            setErrorMessage(`メールの送信に失敗しました。時間をおいて再度お試しください。`);
        }
    }
  };

  return (
    <AuthLayout>
      <div className="flex justify-between items-center mb-8 px-4">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-600 mb-1"></div>
          <span className="text-[10px] font-bold text-slate-800">情報入力</span>
        </div>
        <div className="flex-1 h-[1px] bg-blue-600 mx-2 -mt-3"></div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-600 mb-1"></div>
          <span className="text-[10px] font-bold text-slate-800">メールアドレス認証</span>
        </div>
        <div className="flex-1 h-[1px] bg-gray-300 mx-2 -mt-3"></div>
        <div className="flex flex-col items-center">
           <div className="w-2.5 h-2.5 rounded-full border border-gray-400 bg-white mb-1"></div>
           <span className="text-[10px] text-gray-400">初期設定</span>
        </div>
      </div>

      <div className="relative p-2">
        <div 
            className="absolute inset-0 border-[3px] border-blue-500 rounded-[4px] pointer-events-none"
            style={{ borderRadius: '4px' }}
        ></div>

        <div className="relative px-6 py-12 text-center">
            <div className="mx-auto w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6 border border-blue-100">
                <Mail size={32} />
            </div>

            <h1 className="text-xl font-bold text-slate-800 mb-8">
                認証メールを送信しました
            </h1>
            
            {currentUser?.email && (
                <div className="bg-gray-100 rounded-[4px] py-3 px-4 mb-8 inline-block w-full max-w-xs mx-auto">
                    <p className="font-bold text-slate-800 text-sm break-all">
                        {currentUser.email}
                    </p>
                </div>
            )}

            <div className="text-sm text-slate-600 space-y-4 leading-relaxed mb-10">
                <p>入力されたメールアドレス宛に、<br/>ご本人確認のための認証メールを送信しました。</p>
                <p>メールに記載されているURLをクリックして完了させてください。</p>
            </div>

            {errorMessage && (
                <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-[4px] flex items-center justify-center text-red-600 text-xs font-bold animate-fade-in-up text-left">
                    <AlertCircle className="flex-shrink-0 mr-2" size={18} />
                    <span>{errorMessage}</span>
                </div>
            )}

            <div className="space-y-4 max-w-xs mx-auto">
                <button
                    onClick={handleCheckVerification}
                    disabled={isLoading}
                    className="w-full py-3.5 px-4 bg-blue-600 text-white font-bold rounded-[4px] hover:bg-blue-700 shadow-md transition-all flex items-center justify-center active:scale-95"
                >
                    {isLoading ? <RefreshCw className="animate-spin mr-2" size={20} /> : <CheckCircle2 className="mr-2" size={20} />}
                    認証完了して次へ
                </button>

                <div className="pt-4 border-t border-gray-100 mt-6">
                    <button
                        onClick={handleResendEmail}
                        disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                        className={`text-xs font-bold flex items-center justify-center w-full py-2 transition-colors hover:underline ${
                            resendStatus === 'sent' ? 'text-green-600' : 'text-slate-400 hover:text-blue-600'
                        }`}
                    >
                        {resendStatus === 'sending' && <RefreshCw className="animate-spin mr-1.5" size={12} />}
                        {resendStatus === 'sent' && <CheckCircle2 className="mr-1.5" size={12} />}
                        {resendStatus === 'idle' && '認証メールが届かない場合はこちら'}
                    </button>
                </div>
            </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default VerificationPage;